import { FC } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  HStack,
  Icon,
  Box,
  Divider,
} from '@chakra-ui/react';
import { MdPause, MdClose } from 'react-icons/md';
import { IoWarningOutline } from 'react-icons/io5';

interface DeactivateCampaignConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
  campaignTitle?: string;
}

const DeactivateCampaignConfirmModal: FC<DeactivateCampaignConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
}) => {
  const handleConfirm = async () => {
    await onConfirm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent 
        borderRadius={{ base: 'xl', md: '2xl' }} 
        mx={{ base: 3, md: 4 }} 
        mr={{ base: '70px', md: 4 }}
        maxW={{ base: 'calc(100% - 80px)', md: '480px' }}
        my={{ base: 4, md: 'auto' }}
      >
        <ModalHeader textAlign="center" pt={{ base: 6, md: 10 }} pb={{ base: 3, md: 6 }} px={{ base: 4, md: 8 }}>
          <VStack spacing={{ base: 3, md: 5 }}>
            <Box
              bg="orange.50"
              p={{ base: 3, md: 5 }}
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={MdPause} w={{ base: 8, md: 12 }} h={{ base: 8, md: 12 }} color="orange.500" />
            </Box>
            <Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight="bold" color="gray.800">
              Deactivate Campaign
            </Text>
          </VStack>
        </ModalHeader>

        <ModalBody py={{ base: 3, md: 6 }} px={{ base: 4, md: 8 }}>
          <VStack spacing={{ base: 4, md: 6 }} align="stretch">
            <Text fontSize={{ base: 'xs', md: 'md' }} color="gray.600" textAlign="center" lineHeight="tall">
              Are you sure you want to deactivate this campaign?
            </Text>

            <Divider />

            <HStack
              spacing={3}
              p={{ base: 3, md: 4 }}
              bg="orange.50"
              borderRadius="lg"
              align="flex-start"
            >
              <Box flexShrink={0}>
                <Icon as={IoWarningOutline} boxSize={{ base: 5, md: 6 }} color="orange.500" />
              </Box>
              <VStack align="start" spacing={1}>
                <Text fontSize={{ base: 'xs', md: 'md' }} fontWeight="semibold" color="orange.800">
                  Your ads will be paused
                </Text>
                <Text fontSize={{ base: '10px', md: 'sm' }} color="orange.700" lineHeight="tall">
                  All active ads running on Meta or Google will be paused immediately.
                </Text>
              </VStack>
            </HStack>
          </VStack>
        </ModalBody>

        <ModalFooter pt={{ base: 3, md: 6 }} pb={{ base: 4, md: 8 }} px={{ base: 4, md: 8 }}>
          <VStack spacing={2} w="full">
            <Button
              variant="solid"
              colorScheme="orange"
              onClick={handleConfirm}
              isLoading={isLoading}
              loadingText="Deactivating..."
              leftIcon={<Icon as={MdPause} />}
              size={{ base: 'sm', md: 'lg' }}
              w="full"
            >
              Deactivate & Pause Ads
            </Button>
            <Button
              onClick={onClose}
              isDisabled={isLoading}
              leftIcon={<Icon as={MdClose} />}
              size={{ base: 'sm', md: 'lg' }}
              w="full"
              variant="outline"
            >
              Cancel
            </Button>
          </VStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default DeactivateCampaignConfirmModal;
